import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Home, Nature } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface GardenForm {
  name: string;
  type: 'indoor' | 'outdoor';
  description?: string;
}

interface Props {
  onComplete: () => void;
}

const StartWithGarden: React.FC<Props> = ({ onComplete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [gardenType, setGardenType] = useState<'indoor' | 'outdoor'>('outdoor');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<GardenForm>();

  const onSubmit = async (data: GardenForm) => {
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/onboarding/start-with-garden', {
        ...data,
        type: gardenType,
      });
      toast.success('🌻 Your garden has been created!');
      
      // Navigate to completion page
      navigate('/onboarding/complete', {
        state: {
          entity: response.data.garden,
          entityType: 'garden',
          xpAwarded: response.data.xpAwarded || 50
        }
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create garden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 4 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h2" sx={{ fontSize: '3rem', mb: 2 }}>
          🏡
        </Typography>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Create Your Garden
        </Typography>
        <Typography variant="body1" color="text.secondary">
          A garden is a collection of plants in one area
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          fullWidth
          label="Garden Name"
          margin="normal"
          {...register('name', {
            required: 'Please name your garden',
          })}
          error={!!errors.name}
          helperText={errors.name?.message || 'e.g., "Herb Garden", "Balcony Garden", "Vegetable Patch"'}
        />

        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Is this an indoor or outdoor garden?
          </Typography>
          <ToggleButtonGroup
            value={gardenType}
            exclusive
            onChange={(_, value) => value && setGardenType(value)}
            fullWidth
          >
            <ToggleButton value="indoor" sx={{ py: 2 }}>
              <Home sx={{ mr: 1 }} />
              Indoor
            </ToggleButton>
            <ToggleButton value="outdoor" sx={{ py: 2 }}>
              <Nature sx={{ mr: 1 }} />
              Outdoor
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TextField
          fullWidth
          label="Description (Optional)"
          margin="normal"
          multiline
          rows={3}
          {...register('description')}
          helperText="What will you grow here? Any special features?"
        />

        <Box mt={4}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Create My Garden'}
          </Button>
        </Box>
      </form>

      <Box mt={3} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          💡 Tip: You can add plants to your garden right after creating it!
        </Typography>
      </Box>
    </Paper>
  );
};

export default StartWithGarden;